import { AccessCodeStatus } from '@evaltick/shared';
import { BaseAPIClient } from './base';

// =============================================================================
// TYPES
// =============================================================================

export interface AccessCode {
  id: string;
  code: string;
  packageId: string;
  tags: string[];
  expiresAt: string;
  revokedAt: string | null;
  createdById: number;
  createdAt: string;
  /** When the code was redeemed, or null. Stays populated after a revoke, when `status` reads Revoked. */
  redeemedAt: string | null;
  status: AccessCodeStatus;
}

export interface CreateAccessCodePayload {
  packageId: string;
  tags?: string[];
  expiresInHours?: number;
}

export interface PaginatedAccessCodes {
  data: AccessCode[];
  pagination: {
    page: number;
    limit: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// =============================================================================
// SERVICE
// =============================================================================

export class AdminAccessCodesService extends BaseAPIClient {
  /**
   * Admin: one page of a package's codes, newest first, including used, revoked and
   * expired ones. A cohort can run to any size, so callers paginate against
   * `pagination` rather than assuming one request covers every code.
   */
  async list(packageId: string, page: number, limit: number): Promise<PaginatedAccessCodes> {
    return this.get<PaginatedAccessCodes>(
      `/admin/access-codes?packageId=${encodeURIComponent(packageId)}&page=${page}&limit=${limit}`
    );
  }

  /** Admin: mint one single-use code — the "+ Add code" button. */
  async create(payload: CreateAccessCodePayload): Promise<AccessCode> {
    return this.post<AccessCode>('/admin/access-codes', payload);
  }

  /** Admin: replace a code's tags. Editable for the code's whole life — they carry no authorization. */
  async updateTags(id: string, tags: string[]): Promise<AccessCode> {
    return this.patch<AccessCode>(`/admin/access-codes/${id}`, { tags });
  }

  /** Admin: revoke a code — blocks future redemptions, and cuts the guest's live session if it was already redeemed. */
  async revoke(id: string): Promise<AccessCode> {
    return this.patch<AccessCode>(`/admin/access-codes/${id}/revoke`);
  }
}

export default AdminAccessCodesService;
